import { IntlShape } from "react-intl";
import { FieldValues, UseFormGetValues, UseFormSetError } from "react-hook-form";

import { AttachmentBucket } from "types/Common";
import { getPastSelectableDateErrorPath, isDeadlineDateInPast } from "@RequestFormV2/validation/utils/isDeadlineDateInPast";
import { validateQuotingDeadlineMinimum } from "@RequestFormV2/validation/utils/quotingDeadlineMinimum";
import {
  setPastSelectableDateError,
  setRequiredFieldError,
  setTimeFieldError,
} from "@RequestFormV2/validation/utils/setFieldErrors";

export const INVITED_PROVIDERS_ERROR_PATH = "invited_providers.providers";

type BucketFileEntry = {
  id: number;
  bucket_name?: string;
};

export function validateProvider(
  getValues: UseFormGetValues<FieldValues>,
  setError: UseFormSetError<FieldValues>,
  intl: IntlShape,
  options: {
    bucketAttachments: AttachmentBucket[];
    hasAttachmentBuckets: boolean;
    skipProviderSelection?: boolean;
    skipDeadlines?: boolean;
    quotingDeadlineMinimumMinutes?: number | null;
    preferredTimezone?: string | null;
  },
): boolean {
  let isValid = true;
  const attachmentRequiredMessage = intl.formatMessage({
    id: "request-form.provider.uploadFreightDocuments.attachmentRequired",
    defaultMessage: "Attachment is required",
  });

  if (!options.skipDeadlines) {
    const quotingDeadlineDate = getValues("shipment.quoting_deadline_date");
    const quotingDatePresent = setRequiredFieldError(setError, "shipment.quoting_deadline_date", quotingDeadlineDate, intl);
    if (!quotingDatePresent) {
      isValid = false;
    } else if (
      !setPastSelectableDateError(
        setError,
        getPastSelectableDateErrorPath("shipment.quoting_deadline_date"),
        quotingDeadlineDate,
        intl,
      )
    ) {
      isValid = false;
    }
    const quotingTimeOk = setTimeFieldError(
      setError,
      "shipment.quoting_deadline_time",
      getValues("shipment.quoting_deadline_time"),
      intl,
    );
    if (!quotingTimeOk) {
      isValid = false;
    } else if (
      quotingDatePresent &&
      !isDeadlineDateInPast(quotingDeadlineDate, options.preferredTimezone) &&
      !validateQuotingDeadlineMinimum(
        getValues,
        setError,
        intl,
        options.quotingDeadlineMinimumMinutes,
        options.preferredTimezone,
      )
    ) {
      isValid = false;
    }
    const confirmationDate = getValues("shipment.confirmation_date");
    if (!setRequiredFieldError(setError, "shipment.confirmation_date", confirmationDate, intl)) {
      isValid = false;
    } else if (
      !setPastSelectableDateError(
        setError,
        getPastSelectableDateErrorPath("shipment.confirmation_date"),
        confirmationDate,
        intl,
      )
    ) {
      isValid = false;
    }
    if (!setTimeFieldError(setError, "shipment.confirmation_time", getValues("shipment.confirmation_time"), intl)) {
      isValid = false;
    }
  }

  if (!options.skipProviderSelection) {
    const providers = (getValues("invited_providers.providers") as string[] | undefined) ?? [];
    if (providers.length === 0) {
      setError(INVITED_PROVIDERS_ERROR_PATH, {
        type: "manual",
        message: intl.formatMessage({
          id: "request-form.validation.selectProvider",
          defaultMessage: "Select at least one provider",
        }),
      });
      isValid = false;
    }
  }

  if (options.hasAttachmentBuckets) {
    const uploadedFiles = (getValues("bucket_attachments") as BucketFileEntry[] | undefined) ?? [];
    const mandatoryBuckets = options.bucketAttachments.filter(
      (bucket) => bucket.is_active && bucket.is_mandatory && bucket.bucket_type !== "other",
    );

    mandatoryBuckets.forEach((bucket) => {
      const hasFile = uploadedFiles.some((file) => file.id === bucket.id || file.bucket_name === bucket.bucket_name);

      if (!hasFile) {
        setError(`bucket_attachments.${bucket.id}`, {
          type: "manual",
          message: attachmentRequiredMessage,
        });
        isValid = false;
      }
    });
  }

  return isValid;
}
